import { applyTheme } from '../theme';
import type { ConsentConfig, ConsentState, PolicyBlock } from '../types';

type Handlers = {
  onAcceptAll: () => void;
  onEssentialOnly: () => void;
  onSavePreferences: (categories: Record<string, boolean>) => void;
};

type ModalView = 'main' | 'preferences';

let styleEl: HTMLStyleElement | null = null;
let entryEl: HTMLElement | null = null;
let modalEl: HTMLElement | null = null;

export function ensureStyles(): void {
  if (styleEl) return;
  styleEl = document.createElement('style');
  styleEl.setAttribute('data-orbit-style', 'true');
  styleEl.textContent = `
    .orbit-entry-icon, .orbit-entry-chip {
      position: fixed; z-index: 2147483645;
      top: var(--orbit-entry-top, auto); bottom: var(--orbit-entry-bottom, 20px);
      left: var(--orbit-entry-left, 20px); right: var(--orbit-entry-right, auto);
      font-family: var(--orbit-font); cursor: pointer; border: 0;
    }
    .orbit-entry-icon {
      width: 48px; height: 48px; border-radius: 999px;
      background: var(--orbit-primary); color: var(--orbit-on-primary);
      display: grid; place-items: center; box-shadow: 0 10px 30px rgba(0,0,0,.35);
    }
    .orbit-entry-chip {
      display: inline-flex; align-items: center; gap: 8px;
      background: var(--orbit-surface); color: var(--orbit-text);
      border: 1px solid var(--orbit-border); border-radius: 999px;
      padding: 10px 14px; font-size: 13px; font-weight: 500;
      box-shadow: 0 10px 30px rgba(0,0,0,.35);
    }
    .orbit-entry-chip svg, .orbit-entry-icon svg { width: 24px; height: 24px; }
    .orbit-entry-bar {
      position: fixed; z-index: 2147483645; left: 0; right: 0; bottom: 0;
      background: var(--orbit-surface); color: var(--orbit-text);
      border-top: 1px solid var(--orbit-border);
      font-family: var(--orbit-font);
      display: flex; align-items: center; justify-content: space-between; gap: 16px;
      padding: 14px 20px; box-shadow: 0 -10px 40px rgba(0,0,0,.35);
      flex-wrap: wrap;
    }
    .orbit-entry-bar--clickable { cursor: pointer; }
    .orbit-entry-bar-copy { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1 1 200px; }
    .orbit-entry-bar-copy svg { width: 20px; height: 20px; flex-shrink: 0; color: var(--orbit-primary); }
    .orbit-entry-bar--top {
      bottom: auto; top: 0; border-top: 0; border-bottom: 1px solid var(--orbit-border);
      box-shadow: 0 10px 40px rgba(0,0,0,.35);
    }
    .orbit-entry-bar strong { font-size: 14px; font-weight: 600; letter-spacing: -0.01em; }
    .orbit-entry-bar span { color: var(--orbit-muted); font-size: 13px; }
    .orbit-entry-bar em {
      color: var(--orbit-on-primary); font-weight: 600; font-size: 13px; font-style: normal;
      background: var(--orbit-primary); border-radius: var(--orbit-radius);
      padding: 8px 14px;
    }
    .orbit-entry-bar-actions {
      display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 8px;
      flex: 0 1 auto;
    }
    .orbit-entry-bar-actions button {
      border: 0; border-radius: var(--orbit-radius); padding: 10px 14px;
      font-weight: 600; cursor: pointer; font-size: 13px; font-family: inherit;
      letter-spacing: -0.01em; white-space: nowrap;
    }
    .orbit-modal-root {
      position: fixed; inset: 0; z-index: 2147483646; background: var(--orbit-overlay);
      display: grid; place-items: center; padding: 20px;
      font-family: var(--orbit-font); color: var(--orbit-text);
    }
    .orbit-modal-root--bare { background: transparent; pointer-events: none; }
    .orbit-modal-root--bare .orbit-modal { pointer-events: auto; }
    .orbit-modal-root--anchor {
      display: block; padding: 0; background: transparent; pointer-events: none;
    }
    .orbit-modal-root--anchor.orbit-modal-root--dim {
      background: var(--orbit-overlay); pointer-events: auto;
    }
    .orbit-modal-root--anchor .orbit-modal {
      position: fixed; pointer-events: auto; margin: 0;
    }
    .orbit-modal {
      width: min(var(--orbit-modal-width), 100%); max-height: min(80vh, 720px);
      display: flex; flex-direction: column; overflow: hidden;
      background: var(--orbit-surface); border: 1px solid var(--orbit-border);
      border-radius: var(--orbit-radius-lg);
      box-shadow: var(--orbit-shadow);
    }
    .orbit-modal-head { padding: 24px 24px 0; }
    .orbit-modal-body { flex: 1 1 auto; overflow: auto; padding: 0 24px 4px; }
    .orbit-modal h2 { margin: 0 0 16px; font-size: 1.35rem; letter-spacing: -0.02em; font-weight: 650; }
    .orbit-modal h3 { margin: 18px 0 8px; font-size: 1rem; }
    .orbit-modal p, .orbit-modal li { color: var(--orbit-muted); font-size: .92rem; line-height: 1.55; margin: 0 0 8px; }
    .orbit-modal ul { padding-left: 1.2rem; margin: 0 0 8px; }
    .orbit-prefs { margin-top: 4px; padding-top: 4px; }
    .orbit-prefs-intro { color: var(--orbit-muted); font-size: .9rem; line-height: 1.5; margin: 0 0 14px; }
    .orbit-pref-row {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      padding: 12px 0; border-bottom: 1px solid var(--orbit-border);
    }
    .orbit-pref-row:last-child { border-bottom: 0; }
    .orbit-pref-row label { display: flex; align-items: flex-start; gap: 10px; font-size: .9rem; cursor: pointer; min-width: 0; }
    .orbit-pref-row input { width: 16px; height: 16px; margin-top: 2px; flex-shrink: 0; accent-color: var(--orbit-primary); }
    .orbit-pref-hint { color: var(--orbit-muted); opacity: .75; font-size: 11px; }
    .orbit-modal-actions {
      display: flex; flex-wrap: wrap; gap: 10px;
      padding: 16px 24px 20px; border-top: 1px solid var(--orbit-border);
      background: var(--orbit-surface);
    }
    .orbit-modal-actions button {
      border: 0; border-radius: var(--orbit-radius); padding: 11px 16px;
      font-weight: 600; cursor: pointer; font-size: .9rem; font-family: inherit;
      letter-spacing: -0.01em;
    }
    .orbit-btn-accept { background: var(--orbit-primary); color: var(--orbit-on-primary); }
    .orbit-btn-accept:hover { background: var(--orbit-primary-hover); }
    .orbit-btn-essential { background: transparent; color: var(--orbit-text); border: 1px solid var(--orbit-border) !important; }
    .orbit-btn-edit { background: var(--orbit-surface-alt); color: var(--orbit-text); border: 1px solid var(--orbit-border) !important; }
    .orbit-btn-save { background: var(--orbit-primary); color: var(--orbit-on-primary); }
    .orbit-btn-save:hover { background: var(--orbit-primary-hover); }
    .orbit-btn-back { background: transparent; color: var(--orbit-text); border: 1px solid var(--orbit-border) !important; }
    .orbit-rich { color: var(--orbit-muted); font-size: .92rem; line-height: 1.55; }
    .orbit-rich a { color: var(--orbit-primary); text-decoration: underline; text-underline-offset: 2px; }
    .orbit-rich h1 { margin: 0 0 .6rem; font-size: 1.25rem; font-weight: 650; color: var(--orbit-text); letter-spacing: -.02em; }
    .orbit-rich h2, .orbit-rich h3 { margin: 1rem 0 .45rem; font-size: 1.05rem; font-weight: 650; color: var(--orbit-text); }
    .orbit-rich p { margin: 0 0 .75rem; }
    .orbit-rich ul { margin: 0 0 .75rem; padding-left: 1.2rem; list-style: disc; }
    .orbit-rich ol { margin: 0 0 .75rem; padding-left: 1.2rem; list-style: decimal; }
    .orbit-rich li { margin: .15rem 0; }
    .orbit-rich blockquote {
      margin: 0 0 .75rem; padding-left: .85rem; border-left: 3px solid var(--orbit-border);
      color: var(--orbit-muted); font-style: italic;
    }
    .orbit-rich code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: .85em; background: var(--orbit-surface-2, rgba(0,0,0,.06));
      border-radius: 4px; padding: .1rem .35rem;
    }
    .orbit-rich pre {
      margin: 0 0 .75rem; padding: .75rem .9rem; overflow-x: auto; border-radius: 8px;
      background: var(--orbit-surface-2, rgba(0,0,0,.08)); font-size: .85rem;
    }
    .orbit-rich pre code { background: none; padding: 0; }
    .orbit-rich hr { border: 0; border-top: 1px solid var(--orbit-border); margin: 1rem 0; }
    .orbit-rich u { text-decoration: underline; }
    .orbit-rich s { text-decoration: line-through; }
    .orbit-rich strong { font-weight: 650; color: var(--orbit-text); }
  `;
  document.head.appendChild(styleEl);
}

function cookieIconSvg(): string {
  return `<svg class="floating-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13.5a8 8 0 1 1-9.5-9.2 4 4 0 0 0 5.2 5.2A8 8 0 0 1 20 13.5Z"/><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/><circle cx="13.5" cy="15.5" r="1" fill="currentColor" stroke="none"/><circle cx="12" cy="10" r="1" fill="currentColor" stroke="none"/></svg>`;
}

export type EntryHandlers = {
  onOpen: () => void;
  onAcceptAll?: () => void;
  onEssentialOnly?: () => void;
  onPreferences?: () => void;
};

export type EntryRenderOptions = {
  /** @deprecated Bars hide after a choice; kept for call-site compatibility. */
  compact?: boolean;
};

export function isBarStyle(style: string): boolean {
  return style === 'bottom_bar' || style === 'top_bar';
}

export function usesInlineBarActions(config: ConsentConfig): boolean {
  return isBarStyle(config.entry.style) && config.entry.bar_interaction === 'inline_actions';
}

function shouldAnchorModal(config: ConsentConfig): boolean {
  return Boolean(config.theme.modal_anchor) && !isBarStyle(config.entry.style);
}

export function renderEntry(
  config: ConsentConfig,
  handlers: EntryHandlers | (() => void),
  options: EntryRenderOptions = {},
): void {
  ensureStyles();
  applyTheme(config.theme);
  removeEntry();

  const resolved: EntryHandlers =
    typeof handlers === 'function' ? { onOpen: handlers } : handlers;
  const style = config.entry.style;
  const inline = usesInlineBarActions(config);

  if (isBarStyle(style)) {
    if (inline && !options.compact) {
      renderInlineActionBar(config, style === 'top_bar', resolved);
      return;
    }

    const bar = document.createElement('div');
    bar.className =
      (style === 'top_bar' ? 'orbit-entry-bar orbit-entry-bar--top' : 'orbit-entry-bar') +
      ' orbit-entry-bar--clickable';
    bar.innerHTML =
      `<div class="orbit-entry-bar-copy">` +
      `${cookieIconSvg()}` +
      `<div><strong>${escapeHtml(config.entry.label)}</strong>` +
      `<div><span>${inline ? 'Manage cookie preferences' : 'Review cookie preferences'}</span></div></div></div><em>${inline ? 'Manage' : 'Open'}</em>`;
    bar.addEventListener('click', resolved.onOpen);
    entryEl = bar;
    document.body.appendChild(bar);
    return;
  }

  if (style === 'icon') {
    const btn = document.createElement('button');
    btn.className = 'orbit-entry-icon';
    btn.type = 'button';
    btn.setAttribute('aria-label', config.entry.label);
    btn.innerHTML = cookieIconSvg();
    btn.addEventListener('click', resolved.onOpen);
    entryEl = btn;
    document.body.appendChild(btn);
    return;
  }

  const chip = document.createElement('button');
  chip.className = 'orbit-entry-chip';
  chip.type = 'button';
  chip.innerHTML = `${cookieIconSvg()}<span>${escapeHtml(config.entry.label)}</span>`;
  chip.addEventListener('click', resolved.onOpen);
  entryEl = chip;
  document.body.appendChild(chip);
}

function renderInlineActionBar(
  config: ConsentConfig,
  top: boolean,
  handlers: EntryHandlers,
): void {
  const bar = document.createElement('div');
  bar.className = top ? 'orbit-entry-bar orbit-entry-bar--top' : 'orbit-entry-bar';

  const copy = document.createElement('div');
  copy.className = 'orbit-entry-bar-copy';
  copy.innerHTML =
    `${cookieIconSvg()}` +
    `<div><strong>${escapeHtml(config.entry.label)}</strong>` +
    `<div><span>Choose what this site may use</span></div></div>`;

  const actions = document.createElement('div');
  actions.className = 'orbit-entry-bar-actions';

  const accept = document.createElement('button');
  accept.type = 'button';
  accept.className = 'orbit-btn-accept';
  accept.textContent = config.actions.accept_all;
  accept.addEventListener('click', (event) => {
    event.stopPropagation();
    handlers.onAcceptAll?.();
  });

  const essential = document.createElement('button');
  essential.type = 'button';
  essential.className = 'orbit-btn-essential';
  essential.textContent = config.actions.essential_only;
  essential.addEventListener('click', (event) => {
    event.stopPropagation();
    handlers.onEssentialOnly?.();
  });

  actions.append(accept, essential);

  if (config.entry.show_preferences && handlers.onPreferences) {
    const prefs = document.createElement('button');
    prefs.type = 'button';
    prefs.className = 'orbit-btn-edit';
    prefs.textContent = config.actions.edit_preferences || 'Edit preferences';
    prefs.addEventListener('click', (event) => {
      event.stopPropagation();
      handlers.onPreferences?.();
    });
    actions.append(prefs);
  }

  bar.append(copy, actions);
  entryEl = bar;
  document.body.appendChild(bar);
}

export function openModal(
  config: ConsentConfig,
  current: ConsentState | null,
  handlers: Handlers,
  options: { dismissible?: boolean; view?: ModalView; preferencesOnly?: boolean } = {},
): void {
  ensureStyles();
  applyTheme(config.theme);
  closeModal();

  const dismissible = options.dismissible !== false;
  const preferencesOnly = Boolean(options.preferencesOnly);
  const anchor = shouldAnchorModal(config);
  let view: ModalView = options.view || 'main';
  let working: ConsentState | null = current;
  const checks = new Map<string, HTMLInputElement>();

  modalEl = document.createElement('div');
  const classes = ['orbit-modal-root'];
  if (anchor) {
    classes.push('orbit-modal-root--anchor');
    if (config.theme.overlay) classes.push('orbit-modal-root--dim');
  } else if (!config.theme.overlay) {
    classes.push('orbit-modal-root--bare');
  }
  modalEl.className = classes.join(' ');
  modalEl.setAttribute('role', 'dialog');
  modalEl.setAttribute('aria-modal', 'true');

  if (dismissible) {
    modalEl.addEventListener('click', (e) => {
      if (e.target === modalEl) closeModal();
    });
  }

  const card = document.createElement('div');
  card.className = 'orbit-modal';
  modalEl.appendChild(card);
  document.body.appendChild(modalEl);

  if (anchor) {
    positionAnchoredModal(card, config.theme.entry_position);
  }

  function collectCategories(): Record<string, boolean> {
    const categories: Record<string, boolean> = {};
    for (const category of config.categories) {
      const el = checks.get(category.id);
      if (el) {
        categories[category.id] = category.required ? true : Boolean(el.checked);
      } else if (category.required) {
        categories[category.id] = true;
      } else if (category.ask_visitors === false) {
        // Hidden optional: keep whatever was already granted.
        categories[category.id] = Boolean(working?.categories[category.id]);
      } else {
        categories[category.id] = Boolean(working?.categories[category.id]);
      }
    }
    return categories;
  }

  function render(): void {
    card.innerHTML = '';
    checks.clear();

    const head = document.createElement('div');
    head.className = 'orbit-modal-head';
    const title = document.createElement('h2');
    title.textContent =
      view === 'preferences' ? 'Your preferences' : config.modal.title || 'Cookie Policy';
    head.appendChild(title);
    card.appendChild(head);

    const scroll = document.createElement('div');
    scroll.className = 'orbit-modal-body';
    card.appendChild(scroll);

    if (view === 'main') {
      const body = document.createElement('div');
      if (config.modal.mode === 'rich' && config.modal.rich_html) {
        body.className = 'orbit-rich';
        body.innerHTML = config.modal.rich_html;
      } else {
        body.innerHTML = blocksToHtml(config.modal.blocks || []);
      }
      scroll.appendChild(body);
    } else {
      const prefs = document.createElement('div');
      prefs.className = 'orbit-prefs';
      const intro = document.createElement('p');
      intro.className = 'orbit-prefs-intro';
      intro.textContent = 'Choose what this site may use. Essential items stay on.';
      prefs.appendChild(intro);

      for (const category of config.categories) {
        const askVisitors = category.required ? true : category.ask_visitors !== false;
        if (!category.required && !askVisitors) continue;

        const row = document.createElement('div');
        row.className = 'orbit-pref-row';

        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = category.required ? true : Boolean(working?.categories[category.id]);
        input.disabled = Boolean(category.required);
        checks.set(category.id, input);

        const textWrap = document.createElement('span');
        const text = document.createElement('span');
        text.textContent = category.label;
        textWrap.appendChild(text);
        if (category.description) {
          const desc = document.createElement('span');
          desc.className = 'orbit-pref-hint';
          desc.style.display = 'block';
          desc.textContent = category.description;
          textWrap.appendChild(desc);
        }
        label.append(input, textWrap);

        const hint = document.createElement('span');
        hint.className = 'orbit-pref-hint';
        hint.textContent = category.required ? 'Always on' : 'Optional';

        row.append(label, hint);
        prefs.appendChild(row);
      }
      scroll.appendChild(prefs);
    }

    const actions = document.createElement('div');
    actions.className = 'orbit-modal-actions';

    if (view === 'main') {
      const accept = document.createElement('button');
      accept.className = 'orbit-btn-accept';
      accept.type = 'button';
      accept.textContent = config.actions.accept_all;
      accept.addEventListener('click', () => {
        handlers.onAcceptAll();
        closeModal();
      });

      const essential = document.createElement('button');
      essential.className = 'orbit-btn-essential';
      essential.type = 'button';
      essential.textContent = config.actions.essential_only;
      essential.addEventListener('click', () => {
        handlers.onEssentialOnly();
        closeModal();
      });

      const edit = document.createElement('button');
      edit.className = 'orbit-btn-edit';
      edit.type = 'button';
      edit.textContent = config.actions.edit_preferences || 'Edit preferences';
      edit.addEventListener('click', () => {
        view = 'preferences';
        render();
        if (anchor) positionAnchoredModal(card, config.theme.entry_position);
      });

      actions.append(accept, essential, edit);
    } else {
      if (!preferencesOnly) {
        const back = document.createElement('button');
        back.className = 'orbit-btn-back';
        back.type = 'button';
        back.textContent = 'Back';
        back.addEventListener('click', () => {
          // Keep checkbox choices while browsing back to the policy.
          const draft = collectCategories();
          working = {
            accepted: working?.accepted ?? false,
            categories: { ...(working?.categories || {}), ...draft },
            updatedAt: working?.updatedAt || new Date().toISOString(),
            version: working?.version,
            expiresAt: working?.expiresAt,
          };
          view = 'main';
          render();
          if (anchor) positionAnchoredModal(card, config.theme.entry_position);
        });
        actions.append(back);
      }

      const save = document.createElement('button');
      save.className = 'orbit-btn-save';
      save.type = 'button';
      save.textContent = config.actions.save_preferences || 'Save preferences';
      save.addEventListener('click', () => {
        handlers.onSavePreferences(collectCategories());
        closeModal();
      });

      actions.append(save);
    }

    card.appendChild(actions);
  }

  render();
  if (anchor) {
    // Reposition after layout so width/height are known.
    requestAnimationFrame(() => positionAnchoredModal(card, config.theme.entry_position));
  }
}

/** Place the modal card beside the entry button, clamped to the viewport. */
function positionAnchoredModal(card: HTMLElement, position: ConsentConfig['theme']['entry_position']): void {
  const gap = 12;
  const margin = 16;
  const rect = entryEl?.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(card.offsetWidth || 400, vw - margin * 2);
  const height = Math.min(card.offsetHeight || 320, vh - margin * 2);

  const [vertical, horizontal] = position.split('_') as ['top' | 'bottom', 'left' | 'right'];

  let top: number;
  let left: number;

  if (rect) {
    if (vertical === 'bottom') {
      top = rect.top - gap - height;
      if (top < margin) top = Math.min(rect.bottom + gap, vh - margin - height);
    } else {
      top = rect.bottom + gap;
      if (top + height > vh - margin) top = Math.max(margin, rect.top - gap - height);
    }

    if (horizontal === 'left') {
      left = rect.left;
      if (left + width > vw - margin) left = vw - margin - width;
    } else {
      left = rect.right - width;
      if (left < margin) left = margin;
    }
  } else {
    // Fallback when the entry is not mounted: use the configured corner.
    top = vertical === 'top' ? margin : vh - margin - height;
    left = horizontal === 'left' ? margin : vw - margin - width;
  }

  top = Math.max(margin, Math.min(top, vh - margin - height));
  left = Math.max(margin, Math.min(left, vw - margin - width));

  card.style.top = `${Math.round(top)}px`;
  card.style.left = `${Math.round(left)}px`;
  card.style.right = 'auto';
  card.style.bottom = 'auto';
  card.style.width = `${width}px`;
}

export function closeModal(): void {
  modalEl?.remove();
  modalEl = null;
}

export function isModalOpen(): boolean {
  return modalEl !== null;
}

export function removeEntry(): void {
  entryEl?.remove();
  entryEl = null;
}

function blocksToHtml(blocks: PolicyBlock[]): string {
  return blocks
    .map((block) => {
      if (block.type === 'heading') {
        return `<h3>${escapeHtml(block.heading || '')}</h3>`;
      }
      if (block.type === 'paragraph') {
        return `<p>${escapeHtml(block.body || '')}</p>`;
      }
      if (block.type === 'list') {
        const items = (block.items || []).map((item) => `<li>${escapeHtml(item)}</li>`).join('');
        return `<ul>${items}</ul>`;
      }
      return `<h3>${escapeHtml(block.heading || '')}</h3><p>${escapeHtml(block.body || '')}</p>`;
    })
    .join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
